import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import {
  LayoutGrid, FolderKanban, ArrowUpDown, Truck,
  ShieldAlert, SquareDot, Settings,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "./lib/api";
import ErrorBoundary from "./components/common/ErrorBoundary";
import AuthView from "./components/AuthView";
import MFASetup from "./components/MFASetup";
import OnboardingView from "./components/OnboardingView";
import PortfolioView from "./components/PortfolioView";
import HistoryView from "./components/HistoryView";
import ChangeOrdersView from "./components/ChangeOrdersView";
import SikkerhetsView from "./components/SikkerhetsView";
import SettingsModal from "./components/SettingsModal";
import AuditLog from "./components/AuditLog";
import WorkApprovalView from "./components/WorkApprovalView";
import ResetPasswordView from "./components/ResetPasswordView";
import AcceptInviteView from "./components/AcceptInviteView";
import AdminView from "./components/AdminView";
import ProjectsView from "./components/ProjectsView";
import ProjectDetailView from "./components/ProjectDetailView";
import ContractsView from "./components/ContractsView";
import SuppliersView from "./components/SuppliersView";
import SupplierDetailView from "./components/SupplierDetailView";
import Sidebar, { NavItem } from "./components/layout/Sidebar";
import Topbar from "./components/layout/Topbar";
import { TrialBanner, TrialExpiredBlock } from "./components/TrialBanner";
import CookieConsent from "./components/CookieConsent";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import Compliance from "./components/Compliance";

// ════════════════════════════════════════════════════════════════════════
//  ONBOARDING — server-styrt, kjører ÉN gang for selskapets hovedbruker.
//  Trigger = `user.needs_onboarding` fra login/register (backend: selskapets
//  eier + Company.onboarded_at IS NULL). Fullføring persisteres server-side
//  via POST /auth/complete-onboarding → gjentas ikke på ny enhet/nettleser.
// ════════════════════════════════════════════════════════════════════════

// sessionStorage key the onboarding flow uses to survive the Gmail/Outlook
// OAuth redirect (kept in sync with components/OnboardingView.tsx).
const ONBOARDING_RESUME_KEY = "cob_onboarding_resume";

// Are we returning mid-onboarding from an OAuth round-trip?
function onboardingResumePending(): boolean {
  const params = new URLSearchParams(window.location.search);
  const isOAuthReturn = params.has("email_connected") || params.has("email_error");
  return isOAuthReturn && !!sessionStorage.getItem(ONBOARDING_RESUME_KEY);
}

function notificationTargetRoute(type: string): string {
  switch (type) {
    case "karantene":
    case "ukjent_avsender":
    case "analyse_feilet":
    case "duplikat":
      return "/security";
    case "faktura_flagget":
    case "faktura_godkjent":
    case "klar_for_betaling":
      return "/invoices";
    case "arbeid_bekreftet":
    case "arbeid_avvist":
      return "/work-approval";
    default:
      return "/";
  }
}

function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [token, setToken] = useState<string | null>(localStorage.getItem("contrack_token"));
  const [user, setUser] = useState<any>(JSON.parse(localStorage.getItem("contrack_user") || "null"));
  const [sikkerhetCount, setSikkerhetCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<string | undefined>(undefined);
  // Onboarding handoff: once the cinematic flow completes, show the real
  // dashboard with the closing focus-pull scale/fade-in (`.cob-dash-enter`).
  const [onboardingDone, setOnboardingDone] = useState(false);
  const [enteringDash, setEnteringDash] = useState(false);
  // Sticky: once onboarding is shown for this page-load, keep it mounted even
  // after the OAuth-return URL is cleaned, until the flow finishes.
  const onboardingActiveRef = useRef(false);

  const roleLabels: Record<string, string> = { admin: t("roles.admin"), cfo: t("roles.cfo"), project_manager: t("roles.project_manager"), site_manager: t("roles.site_manager") };

  const currentPath = location.pathname;

  useEffect(() => { setSidebarOpen(false); }, [currentPath]);
  // Åpne Settings-modal ved /settings-rute, in-app-navigasjon eller e-post-OAuth-retur
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("email_connected") || params.get("email_error")) {
      // If this is an onboarding OAuth round-trip, let OnboardingView handle the
      // return (resume the cinematic flow) instead of popping the Settings modal.
      if (onboardingActiveRef.current || sessionStorage.getItem(ONBOARDING_RESUME_KEY)) return;
      setSettingsTab("epost"); setSettingsOpen(true);
    } else if (currentPath === "/settings") {
      setSettingsTab(undefined); setSettingsOpen(true);
    }
  }, [currentPath]);

  const closeSettings = () => {
    setSettingsOpen(false);
    if (currentPath === "/settings") navigate("/", { replace: true });
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setSidebarOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Clear the one-shot dashboard focus-pull animation after it plays.
  useEffect(() => {
    if (!enteringDash) return;
    const t = setTimeout(() => setEnteringDash(false), 520);
    return () => clearTimeout(t);
  }, [enteringDash]);

  const handleLogin = (tokenData: string, userData: any, isNewUser: boolean) => {
    localStorage.setItem("contrack_token", tokenData);
    localStorage.setItem("contrack_user", JSON.stringify(userData));
    setToken(tokenData); setUser(userData);
    navigate(isNewUser ? "/onboarding" : "/");
  };

  const handleLogout = () => {
    localStorage.removeItem("contrack_token");
    localStorage.removeItem("contrack_refresh_token");
    localStorage.removeItem("contrack_user");
    setToken(null); setUser(null);
  };

  useEffect(() => {
    if (!token) return;
    const f = async () => {
      try {
        const [sik, notif] = await Promise.all([
          api.get("/portfolio/sikkerhet"),
          api.get("/notifications/count")
        ]);
        setSikkerhetCount(sik.data.summary?.totalt || 0);
        setUnreadCount(notif.data.unread_count || 0);
        setLastSync(new Date());
      } catch {}
    };
    f();
    const i = setInterval(f, 60000);
    return () => clearInterval(i);
  }, [token]);

  const toggleNotifications = async () => {
    setShowNotifications(!showNotifications);
    if (!showNotifications) {
      try {
        const r = await api.get("/notifications/");
        setNotifications(r.data.notifications);
        setUnreadCount(r.data.unread_count);
      } catch {}
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-read");
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {}
  };

  if (currentPath === "/reset-password") return <Routes><Route path="/reset-password" element={<ResetPasswordView />} /></Routes>;
  if (currentPath === "/accept-invite") return <Routes><Route path="/accept-invite" element={<AcceptInviteView />} /></Routes>;
  if (currentPath === "/privacy") return <><PrivacyPolicy /><CookieConsent /></>;
  if (currentPath === "/tos") return <><TermsOfService /><CookieConsent /></>;
  if (!token) return <><AuthView onLogin={handleLogin} /><CookieConsent /></>;

  // MFA enforcement: admin/CFO uten MFA må sette det opp før de kan bruke appen
  if (user?.mfa_enrollment_required) {
    return <MFASetup required onComplete={() => {
      const updated = { ...user, mfa_enabled: true, mfa_enrollment_required: false };
      setUser(updated);
      localStorage.setItem("contrack_user", JSON.stringify(updated));
    }} />;
  }

  // ── Cinematic onboarding flow ──────────────────────────────────────────
  // Shown when the backend flags `user.needs_onboarding` (selskapets hovedbruker,
  // selskap ikke onboardet), a new user is on /onboarding, or we are returning
  // mid-flow from an OAuth round-trip. Stays mounted (sticky) until the flow
  // finishes, then hands off to the real dashboard with the closing focus-pull.
  // DEMO-BRYTER (filming): tving onboarding ved HVER refresh. AV som standard
  // (produksjonsatferd uendret). Slås på KUN lokalt uten kodeendring — via env eller localStorage:
  //   - runtime, ingen restart: localStorage.setItem("cob_demo_onboarding", "1") i konsollen
  //   - build-time env-var: REACT_APP_DEMO_ONBOARDING=1 npm start (krever full restart)
  // Ingen av dem settes i prod-build / hos kunde. Se OPPGAVER_FOR_KUNDE.md (oppgave 0).
  const DEMO_ONBOARDING =
    process.env.REACT_APP_DEMO_ONBOARDING === "1" ||
    (typeof window !== "undefined" && window.localStorage.getItem("cob_demo_onboarding") === "1");
  const wantOnboarding = !onboardingDone &&
    (DEMO_ONBOARDING || user?.needs_onboarding || currentPath === "/onboarding" || onboardingResumePending() || onboardingActiveRef.current);
  if (wantOnboarding) {
    onboardingActiveRef.current = true;
    return (
      <OnboardingView onComplete={(choice) => {
        onboardingActiveRef.current = false;
        // Persister server-side så onboarding ikke gjentas (også på ny enhet), og lagre
        // land + kontraktstype-valget (contract_type styrer matcher-grenen).
        api.post("/auth/complete-onboarding", {
          country: choice.country, contract_type: choice.contractType,
        }).catch(() => {});
        const updated = { ...user, needs_onboarding: false };
        setUser(updated);
        localStorage.setItem("contrack_user", JSON.stringify(updated));
        setOnboardingDone(true);
        setEnteringDash(true);
        navigate("/");
      }} />
    );
  }

  const isAdminCfo = user?.role === "admin" || user?.role === "cfo";

  const workspaces: NavItem[] = [
    { path: "/", label: t("nav.oversikt"), icon: LayoutGrid },
    { path: "/projects", label: t("nav.prosjekter"), icon: FolderKanban },
    { path: "/change-orders", label: t("nav.endringsordrer"), icon: ArrowUpDown },
    { path: "/suppliers", label: t("nav.leverandorer"), icon: Truck },
  ];
  const monitoring: NavItem[] = [
    ...(isAdminCfo ? [{ path: "/security", label: t("nav.sikkerhet"), icon: ShieldAlert, badge: sikkerhetCount }] : []),
    // Arbeidsgodkjenning skjult fra UI (ruten /work-approval + WorkApprovalView beholdt for evt. re-aktivering)
    // Aktivitetslogg flyttet inn i Innstillinger (ruten /audit beholdt for dyplenker)
    ...(user?.is_super_admin ? [{ path: "/admin", label: t("nav.admin"), icon: SquareDot }] : []),
    { path: "/settings", label: t("nav.innstillinger"), icon: Settings },
  ];

  const allItems = [...workspaces, ...monitoring];
  const activeItem = allItems.find(n => n.path === currentPath);
  const pageTitle = activeItem?.label
    || (currentPath.startsWith("/projects/") ? t("nav.prosjekter") : "Contrack");
  const inMonitoring = monitoring.some(n => n.path === currentPath);
  const section = inMonitoring ? t("nav.section_monitoring") : t("nav.section_workspaces");

  return (
    <div className={"h-screen flex bg-canvas overflow-hidden" + (enteringDash ? " cob-dash-enter" : "")}>
      <Sidebar
        workspaces={workspaces}
        monitoring={monitoring}
        currentPath={currentPath}
        onNavigate={(p) => {
          setSidebarOpen(false);
          if (p === "/settings") { setSettingsTab(undefined); setSettingsOpen(true); }
          else navigate(p);
        }}
        user={user}
        roleLabel={roleLabels[user?.role] || user?.role}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <Topbar
          section={section}
          pageTitle={pageTitle}
          onMenu={() => setSidebarOpen(true)}
          onSearch={(q) => navigate(`/invoices${q ? `?search=${encodeURIComponent(q)}` : ""}`)}
          onQuickAction={() => navigate("/invoices")}
          lastSync={lastSync}
          notifications={notifications}
          unreadCount={unreadCount}
          showNotifications={showNotifications}
          onToggleNotifications={toggleNotifications}
          onMarkAllRead={markAllRead}
          onNotificationClick={(n) => { setShowNotifications(false); navigate(notificationTargetRoute(n.type)); }}
        />

        <TrialBanner subscription={user?.subscription} />
        <TrialExpiredBlock subscription={user?.subscription} />
        <CookieConsent />

        <main className="flex-1 overflow-y-auto bg-canvas min-w-0">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-8">
            <Routes>
              <Route path="/" element={<PortfolioView onSikkerhet={() => navigate("/security")} />} />
              <Route path="/projects" element={<ProjectsView />} />
              <Route path="/projects/:id" element={<ProjectDetailView />} />
              <Route path="/invoices" element={<HistoryView />} />
              <Route path="/contracts" element={<ContractsView />} />
              <Route path="/change-orders" element={<ChangeOrdersView />} />
              <Route path="/suppliers" element={<SuppliersView />} />
              <Route path="/suppliers/:supplierKey" element={<SupplierDetailView />} />
              <Route path="/security" element={isAdminCfo ? <SikkerhetsView /> : <Navigate to="/" replace />} />
              <Route path="/work-approval" element={<WorkApprovalView />} />
              <Route path="/audit" element={<AuditLog />} />
              <Route path="/settings" element={<></>} />
              <Route path="/compliance" element={<Compliance />} />
              {user?.is_super_admin && <Route path="/admin" element={<AdminView />} />}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </main>
      </div>

      <SettingsModal
        open={settingsOpen}
        onClose={closeSettings}
        defaultTab={settingsTab}
        onUserUpdate={(u) => setUser(u)}
      />
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
